// A premium, brand-aware loading state shared across the app's full-screen and
// embedded loading moments. It layers an ambient brand glow, expanding "sonar"
// rings, a smooth dual gradient-ring spinner led by a glowing comet dot, and a
// breathing brand core — tinted with the community's primary color so it feels
// native to each space.
//
// Props:
//   label    – text under the spinner (default "Loading…")
//   primary  – brand color used for the ring + core (hex)
//   embedded – render inline (no full-height background) instead of full-screen
//   logo     – optional image URL shown inside the spinner instead of the core
export default function PageLoader({
  label = "Loading…",
  primary = "#7C3AED",
  embedded = false,
  logo,
}) {
  // Split a trailing ellipsis off the label so we can animate the dots instead.
  const text = (label || "").replace(/[.…]+\s*$/, "");
  return (
    <div
      className={`${
        embedded ? "py-24" : "min-h-screen"
      } relative flex flex-col items-center justify-center gap-7 overflow-hidden animate-fade-in`}
      style={
        embedded
          ? undefined
          : {
              background: `radial-gradient(120% 120% at 50% 35%, #ffffff 0%, #f6f7f9 55%, #eef0f4 100%)`,
            }
      }
    >
      {/* Self-contained keyframes so the loader never depends on a Tailwind
          config rebuild for its sonar rings + reverse-spinning outer arc. */}
      <style>{`
        @keyframes pl-ripple { 0% { transform: scale(0.7); opacity: 0.5; } 100% { transform: scale(2.1); opacity: 0; } }
        @keyframes pl-spin-reverse { to { transform: rotate(-360deg); } }
      `}</style>
      <div className="relative flex h-24 w-24 items-center justify-center">
        {/* Ambient brand glow that softly breathes behind the spinner */}
        <div
          className="absolute -inset-4 rounded-full blur-2xl animate-breathe"
          style={{
            background: `radial-gradient(circle, ${primary}40 0%, transparent 70%)`,
          }}
        />

        {/* Expanding sonar rings (the second is offset for a continuous pulse) */}
        <span
          className="absolute h-16 w-16 rounded-full"
          style={{ border: `1.5px solid ${primary}`, animation: "pl-ripple 2s ease-out infinite" }}
          aria-hidden="true"
        />
        <span
          className="absolute h-16 w-16 rounded-full"
          style={{ border: `1.5px solid ${primary}`, animation: "pl-ripple 2s ease-out infinite", animationDelay: "1s" }}
          aria-hidden="true"
        />

        {/* Spinner stack */}
        <div className="relative h-16 w-16">
          {/* Faint full track for depth */}
          <div
            className="absolute inset-0 rounded-full"
            style={{ border: `3px solid ${primary}14` }}
          />

          {/* Outer thin ring rotating in reverse for layered motion */}
          <div
            className="absolute -inset-[3px] rounded-full"
            style={{
              animation: "pl-spin-reverse 1.6s linear infinite",
              background: `conic-gradient(from 0deg, transparent 0deg, ${primary}55 120deg, transparent 240deg)`,
              WebkitMask:
                "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
            }}
          />

          {/* Main rotating arc + a glowing comet dot leading it */}
          <div className="absolute inset-0 animate-spin" style={{ animationDuration: "0.95s" }}>
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(from 90deg, transparent 0deg, ${primary} 300deg)`,
                WebkitMask:
                  "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
                mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
              }}
            />
            <span
              className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ backgroundColor: primary, boxShadow: `0 0 10px ${primary}` }}
            />
          </div>

          {/* Center: logo if provided, otherwise a breathing brand core */}
          {logo ? (
            <img
              src={logo}
              alt=""
              className="absolute inset-[28%] h-[44%] w-[44%] rounded-full object-cover animate-breathe shadow-sm"
            />
          ) : (
            <div
              className="absolute inset-[34%] rounded-full animate-breathe"
              style={{
                background: `radial-gradient(circle at 32% 28%, ${primary} 0%, ${primary}cc 70%)`,
                boxShadow: `0 0 14px ${primary}66`,
              }}
            />
          )}
        </div>
      </div>

      {text && (
        <div className="flex items-center gap-1.5">
          <span
            className="bg-clip-text text-sm font-semibold tracking-wide text-transparent animate-shimmer"
            style={{
              backgroundImage: `linear-gradient(90deg, ${primary}80 0%, ${primary} 45%, ${primary}E6 55%, ${primary}80 100%)`,
              backgroundSize: "200% 100%",
            }}
          >
            {text}
          </span>
          {/* Animated ellipsis: three brand dots bouncing in sequence */}
          <span className="flex items-end gap-0.5 pb-px" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="h-1 w-1 rounded-full animate-breathe"
                style={{ backgroundColor: primary, animationDelay: `${i * 0.18}s` }}
              />
            ))}
          </span>
        </div>
      )}
    </div>
  );
}

// A compact, premium inline spinner that mirrors the page loader's main ring: a
// conic-gradient arc led by a glowing comet dot. It inherits `currentColor`, so
// it renders white inside a primary button and grey/brand when used standalone.
// Pass `size` in px (default 16); positioning/color go through `className`.
export function Spinner({ size = 16, className = "" }) {
  const stroke = Math.max(2, Math.round(size / 8));
  return (
    <span
      role="status"
      aria-label="Loading"
      className={`relative inline-block shrink-0 animate-spin ${className}`}
      style={{ width: size, height: size, animationDuration: "0.7s" }}
    >
      <span
        className="absolute inset-0 rounded-full"
        style={{
          background: "conic-gradient(from 90deg, transparent 0deg, currentColor 300deg)",
          WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${stroke}px), #000 calc(100% - ${stroke}px))`,
          mask: `radial-gradient(farthest-side, transparent calc(100% - ${stroke}px), #000 calc(100% - ${stroke}px))`,
        }}
      />
      <span
        className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: stroke,
          height: stroke,
          background: "currentColor",
          boxShadow: `0 0 ${Math.max(3, stroke * 2)}px currentColor`,
        }}
      />
    </span>
  );
}

// A single shimmering placeholder bar. Uses a moving gradient (animate-shimmer)
// rather than a flat pulse so it reads as actively loading.
function Bar({ className = "" }) {
  return (
    <div
      className={`rounded-md bg-gray-200 animate-shimmer ${className}`}
      style={{
        backgroundImage:
          "linear-gradient(90deg, #e9eaee 0%, #f4f5f7 50%, #e9eaee 100%)",
        backgroundSize: "200% 100%",
      }}
    />
  );
}

// Skeleton stand-in for a feed post card, shown while posts load. Mirrors the
// real PostCard layout (avatar + name, body lines, media block, action row) so
// the transition to loaded content is smooth and non-jarring.
export function PostCardSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <Bar className="h-11 w-11 !rounded-full" />
        <div className="flex-1 space-y-2">
          <Bar className="h-3.5 w-1/3" />
          <Bar className="h-3 w-1/4" />
        </div>
      </div>
      <div className="mt-4 space-y-2.5">
        <Bar className="h-3 w-full" />
        <Bar className="h-3 w-11/12" />
        <Bar className="h-3 w-3/4" />
      </div>
      <Bar className="mt-4 h-44 w-full !rounded-xl" />
      <div className="mt-4 flex gap-6 border-t border-gray-100 pt-4">
        <Bar className="h-3.5 w-16" />
        <Bar className="h-3.5 w-16" />
        <Bar className="h-3.5 w-16" />
      </div>
    </div>
  );
}

// A short stack of post skeletons for the loading feed.
export function FeedSkeleton({ count = 3 }) {
  return (
    <div className="space-y-4 animate-fade-in">
      {Array.from({ length: count }).map((_, i) => (
        <PostCardSkeleton key={i} />
      ))}
    </div>
  );
}
